import { useEffect, useState, useRef, memo } from 'react';

const AutoTyping = memo(({ text, speed = 10, setTypingMethod }) => {
    const [displayedText, setDisplayedText] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const scrollRef = useRef(null);
    const hasTypedRef = useRef(false);

    useEffect(() => {
        if (hasTypedRef.current) return;

        const safeText = text || '';
        
        if (!safeText) {
            setIsTyping(false);
            if (setTypingMethod) setTypingMethod(false);
            return;
        }

        setDisplayedText('');
        setIsTyping(true);
        
        const indexRef = { current: 0 };

        const interval = setInterval(() => {
            if (setTypingMethod) setTypingMethod(true);
            
            if (indexRef.current < safeText.length) {
                const currentIndex = indexRef.current;
                const newChar = safeText[currentIndex];
                
                setDisplayedText((prev) => prev + newChar);
                indexRef.current++;

                if (scrollRef.current) {
                    scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            } else {
                if (setTypingMethod) setTypingMethod(false);
                clearInterval(interval);
                setIsTyping(false);
                hasTypedRef.current = true;
            }
        }, speed);

        return () => {
            clearInterval(interval);
        };
    }, [text, speed, setTypingMethod]);

    return (
        <div style={{ position: 'relative', display: 'block', width: '100%', maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
            <style>
                {`
                    @keyframes blink {
                        0%, 49% { opacity: 1; }
                        50%, 100% { opacity: 0; }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(2px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    .typing-cursor {
                        display: inline-block;
                        width: 2px;
                        height: 1em;
                        background-color: #1E40AF;
                        margin-left: 2px;
                        animation: blink 1s step-end infinite;
                        vertical-align: text-bottom;
                    }
                    
                    .typing-text {
                        animation: fadeIn 0.3s ease-out;
                        display: block;
                        width: 100%;
                        max-width: 100%;
                        overflow-wrap: break-word;
                        word-break: break-word;
                    }
                `}
            </style>
            <span
                ref={scrollRef}
                className="typing-text"
                style={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: '1.6',
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                }} 
                dangerouslySetInnerHTML={{ __html: displayedText }}
            />
            {isTyping && (
                <span className="typing-cursor"></span>
            )}
        </div>
    );
});

AutoTyping.displayName = 'AutoTyping';

export default AutoTyping;
